'use client';

import { HeartIcon as DefaultIcon } from '@heroicons/react/24/outline';
import { HeartIcon as LikedIcon } from '@heroicons/react/24/solid';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { Button, HeroIcon, type ButtonProps } from '@/components/atoms';
import { useAnalytics } from '@/lib/contexts/analytics';
import { useAuthContext } from '@/lib/contexts/auth';
import { FavouriteType, useFavourite } from '@/lib/contexts/favouriting';
import { useDialog } from '@/lib/hooks/auth/useDialog';
import { cn } from '@/lib/utils/tailwind';

interface LikeButtonProps extends ButtonProps {
  contentId: number;
  contentType: keyof typeof FavouriteType;
  showIcon?: boolean;
  iconOnly?: boolean;
  likedContent?: React.ReactNode;
  defaultContent?: React.ReactNode;
  className?: string;
}

const LikeButton = ({
  contentId,
  contentType,
  showIcon = true,
  iconOnly = false,
  likedContent,
  defaultContent,
  className,
  variant = 'ghost',
  ...props
}: LikeButtonProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const { isFavourite, toggleFavourite } = useFavourite();
  const { isAuthenticated, getUserDataField } = useAuthContext();
  const { openDialog } = useDialog();
  const { track } = useAnalytics();

  const newUser = getUserDataField(u => u.new);
  const customerType = !isAuthenticated
    ? 'undefined'
    : !newUser
      ? 'returning'
      : 'new';

  const [isLiked] = useMemo(() => {
    const liked = isFavourite(FavouriteType[contentType], contentId);
    return [liked] as const;
  }, [contentType, contentId, isFavourite]);

  if (!contentId || !contentType) {
    return null;
  }

  const handleLike = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isAuthenticated) {
      openDialog('auth');
      return;
    }
    e.stopPropagation();

    if (!isLiked) {
      track({
        type: 'custom',
        eventName: 'content_liked',
        eventData: {
          locale,
          element: {
            id: `like-button-${contentType}-${contentId}`,
            label: t('ARIA_LABEL_LIKE'),
          },
          content: {
            id: contentId.toString(),
            type: FavouriteType[contentType].toLowerCase(),
          },
          customer: { type: customerType },
        },
      });
    }

    toggleFavourite(FavouriteType[contentType], contentId, locale);
  };

  return (
    <Button
      onClick={handleLike}
      className={cn(className, 'hover:cursor-pointer')}
      aria-label={iconOnly ? t('ARIA_LABEL_LIKE') : undefined}
      aria-pressed={iconOnly ? isLiked : undefined}
      variant={variant}
      {...props}
    >
      {showIcon && <HeroIcon icon={isLiked ? LikedIcon : DefaultIcon} />}
      {!iconOnly &&
        (isLiked
          ? (likedContent ?? t('FOCAL_LIKED'))
          : (defaultContent ?? t('FOCAL_LIKE')))}
    </Button>
  );
};

export { LikeButton, type LikeButtonProps };
